import { useEffect, useMemo, useState } from 'react'

import { LDContext, LDMultiKindContext } from 'launchdarkly-js-client-sdk'
import { noop } from 'lodash-es'

import {
  DEMO_LD_USER_KEY,
  LD_SHARED_CONTEXT_KEYS,
  LDFlags,
  LyraApplicationContext,
} from '@lyrahealth-inc/shared-app-logic'
import { useLDClient } from '@lyrahealth-inc/shared-app-logic/src/features/flags/launchdarkly-client-sdk.web'

import { WebParams } from './types'

/**
 * Get a demo user key if set in local storage
 */
function getDemoUserKey(): string {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(DEMO_LD_USER_KEY)) {
    return localStorage.getItem(DEMO_LD_USER_KEY) as string
  }

  return ''
}

export const useInitiateFeatureFlagging = ({
  country,
  customerName,
  hasFetchedUser,
  user,
  healthPlanEnabled,
  isCoachingEnabled,
  customerHasAssertiveTriage,
  hideDependentField,
  selfSearchAvailable,
  isUserTeen,
  displayLanguage,
  onFlags = noop,
  libraryDisabled,
  libraryEventsDisabled,
  hashedUserId,
  deviceUUID,
  isProd,
  productType,
  genAiOptOut,
  track = noop,
  onSetLDClient = noop,
}: WebParams) => {
  const [isLaunchDarklyIdentified, setIsLaunchDarklyIdentified] = useState<boolean>(false)
  const client = useLDClient()

  useEffect(() => {
    if (client) {
      onSetLDClient(client)
    }
  }, [onSetLDClient, client])
  /**
   * If vendors are disabled, we should use the unknown-user-key so that we do not incur costs for anon context
   */
  const launchDarklyUserId = useMemo(() => {
    if (!user?.id) {
      return LD_SHARED_CONTEXT_KEYS.LAUNCH_DARKLY_USER_UUID
    } else if (isProd) {
      return hashedUserId
    } else {
      return user?.id
    }
  }, [hashedUserId, isProd, user?.id])
  const customerContext: LDContext = useMemo(
    () => ({
      kind: 'customer',
      key: customerName,
      healthPlanEnabled,
      isCoachingEnabled,
      customerHasAssertiveTriage,
      libraryDisabled,
      libraryEventsDisabled,
      hideDependentField,
      productType,
      genAiOptOut,
    }),
    [
      customerName,
      healthPlanEnabled,
      isCoachingEnabled,
      customerHasAssertiveTriage,
      libraryDisabled,
      libraryEventsDisabled,
      hideDependentField,
      productType,
      genAiOptOut,
    ],
  )

  /**
   * For logged out users, identify user by device without explictly passing a key.
   * The launch darkly SDK will auto-generate a device uuid that is consistent and store it in localStorage.
   * After the user is logged in, we will identify the user below with context of multi and associate the auto-generated
   * device context with this user
   *
   * Note from Launch Darkly Docs: If you use a client-side SDK and want to use a unique key for your anonymous contexts,
   * you can defer key generation onto the SDK. To do this, build a context object and omit the key completely.
   * The SDK generates a unique identifier for the context and persists this unique identifier in local storage.
   * Each subsequent context evaluation without a context key uses that saved unique identifier.
   *
   * https://docs.launchdarkly.com/sdk/features/anonymous#javascript
   */
  const queryParams = new URLSearchParams(window?.location?.search)
  // Add a deviceId query parameter to simulate different device IDs
  const deviceIdParam = queryParams.get('deviceId') || ''
  const deviceContext: LDContext = useMemo(
    () => ({
      kind: 'device',
      /**
       * This device context is anonymous
       * The key is omitted, and the SDK will automatically generate one
       */
      anonymous: true,
      ...((!isProd && deviceIdParam) || deviceUUID ? { key: deviceIdParam || deviceUUID } : {}),
    }),
    [deviceIdParam, deviceUUID, isProd],
  )

  const applicationContext: Omit<LyraApplicationContext, 'nativeApplicationVersion'> = useMemo(
    () => ({
      kind: 'application',
      key: 'lyrahealthwebapp',
      applicationId: 'lyrahealthwebapp',
      applicationName: 'LyraWeb UI',
      platform: 'web',
    }),
    [],
  )

  const userContext: LDContext = useMemo(
    () => ({
      /**
       * Only add the key to the user context once the user is logged in
       * This user context is anonymous
       * The key is omitted, and the SDK will automatically generate one
       *
       */
      key: launchDarklyUserId,
      kind: 'user',
      country,
      demoUserKey: getDemoUserKey(),
      gender: user?.gender?.gender ?? '',
      /**
       * TODO: Update existing feature flags in launch darkly to use attributes from customer instead of user
       * and remove these below that are specific to the customer and already exist in the customerContext above
       */
      customer: customerName ?? '',
      healthPlanEnabled,
      isCoachingEnabled,
      customerHasAssertiveTriage,
      hideDependentField,
      selfSearchAvailable,
      isUserTeen,
      displayLanguage,
      productType,
      _meta: {
        privateAttributes: ['demoUserKey'],
      },
    }),
    [
      country,
      customerHasAssertiveTriage,
      customerName,
      healthPlanEnabled,
      hideDependentField,
      isCoachingEnabled,
      isUserTeen,
      displayLanguage,
      launchDarklyUserId,
      selfSearchAvailable,
      user?.gender?.gender,
      productType,
    ],
  )

  useEffect(() => {
    const onFlagsChangeCallback = (flags: {
      [key: string]: { current: string | boolean | object; previous: string | boolean | object }
    }) => {
      onFlags(
        Object.keys(flags).reduce((acc, curr) => {
          acc[curr] = flags[curr].current
          return acc
        }, {} as LDFlags),
      )
    }

    if (hasFetchedUser) {
      const multiContext: LDMultiKindContext = {
        kind: 'multi',
        user: userContext,
        device: deviceContext,
        customer: customerContext,
        application: applicationContext,
      }
      client?.identify(multiContext, undefined, function (_, flags) {
        console.debug(
          `Vendor - Context flags available for user: ${userContext.key}, device: ${deviceUUID}, application: ${applicationContext?.applicationId}, and customer: ${customerContext.key}`,
        )
        setIsLaunchDarklyIdentified(true)
        onFlags(flags as LDFlags)
      })
      track(userContext.key, {
        ...multiContext,
        message: 'launchDarklyContext',
      })
    }
    // Update the redux store on flag change
    client?.on('change', onFlagsChangeCallback)
    return () => {
      client?.off('change', onFlagsChangeCallback)
    }
  }, [
    client,
    hasFetchedUser,
    onFlags,
    deviceContext,
    customerContext,
    userContext,
    applicationContext,
    deviceUUID,
    track,
  ])

  return isLaunchDarklyIdentified
}
